import { BaseRestApiService } from '@shared/services/base-rest-api.service';
import { DynamicFormService } from '@shared/components/dynamic-form/dynamic-form.service';
import { EventService } from '@shared/event.service';
import { FORM_ID, findFormId, getFormFilesList } from './program-form.service';
import { Injectable } from '@angular/core';
import { map } from 'rxjs/operators';
import { Observable } from 'rxjs';
import { Program, ProgramSubmissionType } from './program.model';
import { ProgramApplication } from './program-application.model';
import { ProgramApplicationConstant } from './program-application.constant';
import { ProgramApplicationFormService } from './program-application-form.service';
import { ProgramApplicationStatus } from './program-application-status.model';
import { RestApiWrapperService } from '@shared/services/rest-api-wrapper.service';
import { TranslateService } from '@ngx-translate/core';
import { HttpParams } from '@angular/common/http';

@Injectable({
  providedIn: 'root'
})
export class ProgramApplicationService extends BaseRestApiService {

  constructor(_restApiWrapperService: RestApiWrapperService,
              private _dynamicFormService: DynamicFormService,
              private _programApplicationFormService: ProgramApplicationFormService,
              private _translateService: TranslateService,
              private _eventService: EventService) {
    super(_restApiWrapperService);
  }

  findProgramApplications(programId: string): Observable<ProgramApplication[]> {
    return this.get<ProgramApplication[]>(ProgramApplicationConstant.application.list(programId), {params: {page: 0, size: 5000}})
    .pipe(
      map((result: ProgramApplication[]) => {
        return result.map((programApplication: ProgramApplication) => new ProgramApplication(programApplication));
      })
    );
  }

  exportResponsesXLS(programId: string, configId: string) {
    const params = new HttpParams();
    const option = {
      responseType: 'blob' as const,
      params
    };
    return this.get<any[]>(ProgramApplicationConstant.application.exportResponsesXLS(programId, configId), option);
  }

  createProgramApplication(program: Program) {
    const programApplication: ProgramApplication = new ProgramApplication({
      program: program
    });
    return this.post(ProgramApplicationConstant.application.update(program.id), programApplication)
      .pipe(map(result => new ProgramApplication(result)));
  }

  createAllProgramApplications(program: Program): Observable<ProgramApplication[]> {
    return this.post(ProgramApplicationConstant.application.createAll(program.id), {});
  }

  getProgramApplication(programId: string, applicationId: string): Observable<ProgramApplication> {
    return this.get(ProgramApplicationConstant.application.detail(programId, applicationId))
      .pipe(
        map(result => new ProgramApplication(result))
      );
  }

  saveApplication(programId: string, fileName: string, json: {}, applicationId: string ): Observable<any> {
    let jsonObject = this._dynamicFormService.removeBlankNodes({...json});
     const formId = findFormId(fileName);
     if (formId) {
      // add Validate button before Submit button
      jsonObject = this._dynamicFormService.checkValidateButton(jsonObject);
       // add submit button when the form is pre-qualification
       if (fileName === getFormFilesList()['preQualification']) {
         jsonObject = this._dynamicFormService.checkSubmitButton(jsonObject);
       }
       // add save button to all forms
       jsonObject = this._dynamicFormService.checkSaveButton(jsonObject);
       return this._programApplicationFormService.saveProgramApplicationForm(programId, applicationId, FORM_ID.PRE_QUALIFICATION, jsonObject);
     } else {
       this._eventService.error(this._translateService.instant('error-message.file-name-not-supported'));
     }
   }

  updateProgramApplicationStatus(application: ProgramApplication, status: string): Observable<ProgramApplication> {
    return this.put(ProgramApplicationConstant.application.status(application.program.id, application.id, status), application)
      .pipe(map(result => new ProgramApplication(result)));
  }

  update(application: ProgramApplication): Observable<ProgramApplication> {
    return this.put(ProgramApplicationConstant.application.detail(application.program.id, application.id), application)
      .pipe(map(result =>  new ProgramApplication(result)));
  }

  getApplicationStatus(statuses: ProgramApplicationStatus[]): string {
    const ordered = statuses?.sort((a, b) => {
      const aDate = a.timestamp ? new Date(a.timestamp).getTime() : new Date(0).getTime();
      const bDate = b.timestamp ? new Date(b.timestamp).getTime() : new Date(0).getTime();
      return bDate - aDate;
    });
    return ordered?.length > 0 ? ordered[0].name : '';
  }
}
